import { useCallback, useEffect, useState } from "react";
import { fetchCurrencyRates } from "./currencyRatesApi";

/**
 * Normalizes one rate row, keeping rate and margin as strings so the exact
 * server-side decimals are never lost to a float round-trip.
 * @param {Object} row - raw rate from the API
 * @returns {Object}
 */
function normalizeRate(row) {
    return {
        id: row.id,
        baseCurrency: row.base_currency || "",
        quoteCurrency: row.quote_currency || "",
        rate: row.rate != null ? String(row.rate) : "",
        marginPercent: row.margin_percent != null ? String(row.margin_percent) : "",
        active: Boolean(row.active),
        modifiedAt: row.modified_at || null,
    };
}

/**
 * Runs the GET request inside an abort-aware wrapper.
 * @param {Object} args - loader arguments
 */
async function loadRates({ signal, setRates, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const data = await fetchCurrencyRates();
        if (!signal.aborted) setRates(Array.isArray(data) ? data.map(normalizeRate) : []);
    } catch (err) {
        if (!signal.aborted) setError(err?.response?.data || "Impossible de charger les taux de change");
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Custom hook fetching every configured exchange rate direction.
 * @returns {{ rates: Array, loading: boolean, error: any, refresh: Function }}
 */
function useCurrencyRates() {
    const [rates, setRates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reloadToken, setReloadToken] = useState(0);

    const refresh = useCallback(() => setReloadToken((token) => token + 1), []);

    useEffect(() => {
        const controller = new AbortController();
        loadRates({ signal: controller.signal, setRates, setError, setLoading });
        return () => controller.abort();
    }, [reloadToken]);

    return { rates, loading, error, refresh };
}

export default useCurrencyRates;
